import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, DialogContentText,
    Checkbox, ListItemText, OutlinedInput, Chip, CircularProgress, Alert, Snackbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import LinkIcon from '@mui/icons-material/Link';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const roles = ['carrier', 'broker', 'shipper', 'Admin'];

const TrainingManagement = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: () => {} });

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'link', 
        linkUrl: '',
        videoFile: null,
        target_audience: []
    });

    const fetchResources = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const res = await axios.get(`${API_BASE_URL}/api/training/admin`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setResources(res.data);
        } catch (err) {
            console.error("Failed to fetch training resources", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResources();
    }, []);

    const handleOpen = () => {
        setEditingId(null);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setEditingId(null);
        setFormData({
            title: '',
            description: '',
            type: 'link',
            linkUrl: '',
            videoFile: null,
            target_audience: []
        });
        setError(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        setFormData({ ...formData, videoFile: e.target.files[0] });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleEditClick = (resource) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Edit',
            message: `Do you want to edit the resource "${resource.title}"?`,
            onConfirm: () => {
                setEditingId(resource.id);
                setFormData({
                    title: resource.title,
                    description: resource.description || '',
                    type: resource.type,
                    linkUrl: resource.type === 'link' ? resource.url : '',
                    videoFile: null,
                    target_audience: resource.target_audience || []
                });
                setOpen(true);
                setConfirmDialog(prev => ({ ...prev, open: false }));
            }
        });
    };

    const handleSubmit = async () => {
        if (!formData.title || formData.target_audience.length === 0) {
            setError("Title and Target Audience are required.");
            return;
        }
        if (formData.type === 'link' && !formData.linkUrl) {
            setError("Link URL is required.");
            return;
        }
       
        if (formData.type === 'video' && !formData.videoFile && !editingId) {
            setError("Video file is required.");
            return;
        }

        setUploading(true);
        const data = new FormData();
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('type', formData.type);
        data.append('target_audience', formData.target_audience.join(','));

        if (formData.type === 'link') {
            data.append('linkUrl', formData.linkUrl);
        } else {
            if (formData.videoFile) {
                data.append('videoFile', formData.videoFile);
            }
        }

        try {
            const token = localStorage.getItem('jwtToken');
            const headers = { 
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            };

            if (editingId) {
                await axios.put(`${API_BASE_URL}/api/training/admin/${editingId}`, data, headers);
            } else {
                await axios.post(`${API_BASE_URL}/api/training/admin`, data, headers);
            }
            fetchResources();
            handleClose();
            setSnackbar({ open: true, message: editingId ? 'Resource updated successfully' : 'Resource added successfully', severity: 'success' });
        } catch (err) {
            console.error("Upload/Update failed", err);
            const msg = err.response?.data?.message || `Failed to ${editingId ? 'update' : 'upload'} resource.`;
            setError(msg);
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteClick = (resource) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Delete',
            message: `Are you sure you want to delete the resource "${resource.title}"?`,
            onConfirm: () => handleDelete(resource.id)
        });
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('jwtToken');
            await axios.delete(`${API_BASE_URL}/api/training/admin/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setResources(resources.filter(r => r.id !== id));
            setSnackbar({ open: true, message: 'Resource deleted successfully', severity: 'success' });
        } catch (err) {
            console.error("Delete failed", err);
            setSnackbar({ open: true, message: 'Failed to delete resource', severity: 'error' });
        } finally {
            setConfirmDialog(prev => ({ ...prev, open: false }));
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Training Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
                    Add Resource
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#11224d' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'white' }}>Title</TableCell>
                            <TableCell sx={{ color: 'white' }}>Type</TableCell>
                            <TableCell sx={{ color: 'white' }}>Audience</TableCell>
                            <TableCell sx={{ color: 'white' }}>Date Added</TableCell>
                            <TableCell sx={{ color: 'white', textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : resources.map((res) => (
                            <TableRow key={res.id}>
                                <TableCell>
                                    <Typography variant="subtitle2" fontWeight="bold">{res.title}</Typography>
                                    <Typography variant="caption" color="textSecondary">{res.description}</Typography>
                                </TableCell>
                                <TableCell>
                                    {res.type === 'video' ? 
                                        <Chip icon={<VideoLibraryIcon />} label="Video" size="small" color="primary" variant="outlined" /> : 
                                        <Chip icon={<LinkIcon />} label="Link" size="small" color="info" variant="outlined" />
                                    }
                                </TableCell>
                                <TableCell>
                                    {res.target_audience && res.target_audience.map(role => (
                                        <Chip key={role} label={role} size="small" sx={{ mr: 0.5, textTransform: 'capitalize' }} />
                                    ))}
                                </TableCell>
                                <TableCell>{new Date(res.created_at).toLocaleDateString()}</TableCell>
                               <TableCell align="right">
    <IconButton color="primary" onClick={() => handleEditClick(res)}>
        <EditIcon />
    </IconButton>
                                <IconButton color="error" onClick={() => handleDeleteClick(res)}>
        <DeleteIcon />
    </IconButton>
</TableCell>
                            </TableRow>
                        ))}
                        {!loading && resources.length === 0 && (
                            <TableRow><TableCell colSpan={5} align="center">No training resources found.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{editingId ? 'Edit' : 'Add'} Training Resource</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <TextField
                        margin="dense" label="Title" name="title" fullWidth
                        value={formData.title} onChange={handleChange}
                    />
                    <TextField
                        margin="dense" label="Description" name="description" fullWidth multiline rows={3}
                        value={formData.description} onChange={handleChange}
                    />
                    
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Type</InputLabel>
                        <Select name="type" value={formData.type} label="Type" onChange={handleChange}>
                            <MenuItem value="link">External Link</MenuItem>
                            <MenuItem value="video">Upload Video</MenuItem>
                        </Select>
                    </FormControl>

                    {formData.type === 'link' ? (
                        <TextField
                            margin="dense" label="Link URL" name="linkUrl" fullWidth
                            value={formData.linkUrl} onChange={handleChange}
                        />
                    ) : (
                        <Box sx={{ mt: 2, mb: 1 }}>
                            <Button variant="outlined" component="label" fullWidth>
                                Upload Video File
                                <input type="file" hidden accept="video/*" onChange={handleFileChange} />
                            </Button>
                            {formData.videoFile && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Selected: {formData.videoFile.name}</Typography>}
                        </Box>
                    )}

                    <FormControl fullWidth margin="dense">
                        <InputLabel>Target Audience</InputLabel>
                        <Select
                            multiple
                            name="target_audience"
                            value={formData.target_audience}
                            onChange={handleChange}
                            input={<OutlinedInput label="Target Audience" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {roles.map((role) => (
                                <MenuItem key={role} value={role}>
                                    <Checkbox checked={formData.target_audience.indexOf(role) > -1} />
                                    <ListItemText primary={role} sx={{ textTransform: 'capitalize' }} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={uploading}>
                        {uploading ? 'Saving...' : (editingId ? 'Save Changes' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
                <DialogTitle>{confirmDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
                    <Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TrainingManagement;